<!-- Copyright (c) 2009-2022. Authors: see NOTICE file.

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.-->

<!-- Ontologies page (design: ims-ontology) — full-width white workspace: left rail (ontology
     list) + center term-tree workspace. Implemented inside the app shell (we keep the global
     navbar; the design's top bar is skipped). -->
<template>
<div class="ont-page">
  <b-loading :is-full-page="false" :active="loading" />

  <template v-if="!loading">
    <div class="box error" v-if="!ontologies">
      <h2> {{ $t('error') }} </h2>
      <p>{{ $t('unexpected-error-info-message') }}</p>
    </div>

    <div v-else-if="ontologies.length > 0" class="ont-ws">
      <!-- left rail -->
      <aside class="ont-rail">
        <div class="ont-rail-new">
          <button v-if="!currentUser.guestByNow" class="ont-new-btn" @click="creationModal = true">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            {{ $t('new-ontology') }}
          </button>
        </div>
        <div class="ont-rail-searchwrap">
          <div class="ont-rail-search">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" stroke-width="2"><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.5" y2="16.5"/></svg>
            <input v-model="searchString" type="search" :placeholder="$t('find-ontology')" />
          </div>
        </div>
        <div class="ont-rail-label">
          <span>{{ $t('ontologies') }}</span>
          <span>{{ filteredOntologies.length }}</span>
        </div>
        <div class="ont-rail-list">
          <a
            v-for="ontology in filteredOntologies"
            :key="ontology.id"
            class="ont-rail-row"
            :class="{active: isSelected(ontology)}"
            @click="selectOntology(ontology)"
          >
            <span class="ont-rail-mark"></span>
            <span class="ont-rail-name">{{ ontology.name }}</span>
          </a>
          <em v-if="!filteredOntologies.length" class="ont-rail-empty">{{ $t('no-result') }}</em>
        </div>
      </aside>

      <!-- center workspace -->
      <main class="ont-main">
        <ontology-details
          v-if="selectedOntology"
          :key="selectedOntology.id"
          :ontology="selectedOntology"
          @delete="deleteOntology()"
          @rename="renameOntology"
        />
        <div v-else class="ont-none">
          <div class="ont-none-title">{{ $t('no-ontology-selected') }}</div>
          <div class="ont-none-sub">{{ $t('create-ontology-to-start') }}</div>
        </div>
      </main>
    </div>

    <div class="box error" v-else>
      <div class="columns">
        <p class="column">{{ $t('dont-have-access-to-any-ontology') }}</p>
        <p class="column has-text-right">
          <button class="button is-link" @click="creationModal = true" v-if="!currentUser.guestByNow">
            {{ $t('new-ontology') }}
          </button>
        </p>
      </div>
    </div>
  </template>

  <add-ontology-modal :active.sync="creationModal" @newOntology="addOntology" />
</div>
</template>

<script>
import {get, sync} from '@/utils/store-helpers';
import {getWildcardRegexp} from '@/utils/string-utils';

import {OntologyCollection} from '@/api';
import OntologyDetails from './OntologyDetails';
import AddOntologyModal from './AddOntologyModal';

export default {
  name: 'list-ontologies',
  components: {
    OntologyDetails,
    AddOntologyModal
  },
  data() {
    return {
      loading: true,
      ontologies: null,
      selectedOntology: null,
      creationModal: false
    };
  },
  computed: {
    currentUser: get('currentUser/user'),
    idTargetOntology() {
      return Number(this.$route.params.idOntology) || this.$store.state.ontologies.selectedOntology;
    },
    filteredOntologies() {
      if (this.searchString.length > 0) {
        let regexp = getWildcardRegexp(this.searchString);
        return this.ontologies.filter(ontology => regexp.test(ontology.name));
      }
      return this.ontologies;
    },
    searchString: sync('ontologies/searchString')
  },
  watch: {
    idTargetOntology() {
      if (!this.idTargetOntology) {
        this.selectedOntology = this.ontologies[0];
        return;
      }
      if (this.selectedOntology && this.selectedOntology.id === this.idTargetOntology) {
        return;
      }
      this.selectTargetOntology();
    },
    selectedOntology(ontology) {
      if (ontology) {
        this.$store.commit('ontologies/setSelectedOntology', ontology.id);
      }
    }
  },
  methods: {
    selectOntology(ontology) {
      this.selectedOntology = ontology;
      this.$router.push(`/ontology/${this.selectedOntology.id}`);
    },
    selectTargetOntology() {
      this.selectedOntology = this.ontologies.find(ontology => ontology.id === this.idTargetOntology);
    },
    isSelected(ontology) {
      return this.selectedOntology && this.selectedOntology.id === ontology.id;
    },
    sortOntologies() {
      this.ontologies.sort((a, b) => a.name.localeCompare(b.name));
    },
    addOntology(ontology) {
      this.ontologies.push(ontology);
      this.sortOntologies();
      this.selectedOntology = ontology;
    },
    renameOntology(newName) {
      this.selectedOntology.name = newName;
      this.sortOntologies();
    },
    async deleteOntology() {
      let index = this.ontologies.findIndex(ontology => ontology.id === this.selectedOntology.id);
      try {
        await this.selectedOntology.delete();
        this.ontologies.splice(index, 1);
        this.$notify({
          type: 'success',
          text: this.$t('notif-success-ontology-deletion', {name: this.selectedOntology.name})
        });
        this.selectedOntology = this.ontologies[0];
      } catch (error) {
        console.log(error);
        this.$notify({type: 'error', text: this.$t('notif-error-ontology-deletion')});
      }
    }
  },
  async created() {
    try {
      this.ontologies = (await OntologyCollection.fetchAll({light: true})).array;
    } catch (error) {
      console.log(error);
      this.loading = false;
      return;
    }
    this.sortOntologies();

    if (this.idTargetOntology) {
      this.selectTargetOntology();
    } else {
      this.selectedOntology = this.ontologies[0];
    }

    this.loading = false;
  }
};
</script>

<style scoped>
.ont-page {
  position: relative;
  height: calc(100vh - 50px);
  background: #fff;
  overflow: hidden;
}
.ont-ws {
  display: flex;
  height: 100%;
  min-height: 0;
}

/* left rail */
.ont-rail {
  width: 300px;
  flex: 0 0 300px;
  border-right: 1px solid #eef1f5;
  display: flex;
  flex-direction: column;
  background: #fafbfd;
  min-height: 0;
}
.ont-rail-new { padding: 18px 18px 12px; }
.ont-new-btn {
  width: 100%;
  height: 42px;
  border: none;
  border-radius: 11px;
  background: linear-gradient(135deg, #1aa9c4, #1e63d0);
  color: #fff;
  font-size: 14.5px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: 0 6px 16px -6px rgba(30, 99, 208, 0.6);
}
.ont-new-btn:hover { filter: brightness(1.06); }
.ont-rail-searchwrap { padding: 0 18px 12px; }
.ont-rail-search {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 38px;
  background: #fff;
  border: 1px solid #e9edf3;
  border-radius: 10px;
  padding: 0 11px;
}
.ont-rail-search input { border: none; background: transparent; outline: none; font-size: 13.5px; color: #334155; width: 100%; }
.ont-rail-label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 22px 8px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1px;
  color: #94a3b8;
  text-transform: uppercase;
}
.ont-rail-list { flex: 1; min-height: 0; overflow-y: auto; padding: 0 12px 16px; display: flex; flex-direction: column; gap: 4px; }
.ont-rail-row {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 10px 12px;
  border-radius: 10px;
  cursor: pointer;
  text-decoration: none;
  color: #1e293b;
}
.ont-rail-row:hover { background: #f1f5fb; }
.ont-rail-mark { width: 3px; height: 20px; border-radius: 3px; background: transparent; flex: 0 0 3px; }
.ont-rail-row.active { background: #eef4ff; }
.ont-rail-row.active .ont-rail-mark { background: linear-gradient(180deg, #1aa9c4, #1e63d0); }
.ont-rail-row.active .ont-rail-name { color: #1e63d0; font-weight: 700; }
.ont-rail-name { flex: 1; min-width: 0; font-size: 14px; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ont-rail-empty { padding: 14px; color: #94a3b8; font-size: 13px; }

/* center */
.ont-main { flex: 1; min-width: 0; display: flex; flex-direction: column; background: #fff; min-height: 0; }
.ont-none { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; color: #94a3b8; }
.ont-none-title { font-size: 18px; font-weight: 700; color: #64748b; }
.ont-none-sub { font-size: 14px; margin-top: 6px; }
</style>
